import {point3d, TextHorizontalAlignment, TextVerticalAlignment} from "@tarikjabiri/dxf";
import {calculateLengthWidth, inch2feet} from "./DxfCalculations";

// Draws the segment edges table in the DXF drawing
export function SegmentDetailsTable(label, data, tableStartX, tableStartY, cellHeight, cellWidths, drawing) {

    let obsColumnData = [];
    obsColumnData.push({index: 'POI', X: 'X', Y: 'Y'}); // Header row

    // Convert coordinates to DXF-safe values and add to data
    for (let i = 1; i < data.length - 1; i++) {
        const element = data[i];
        element.y = (element.y * -1); // Flip Y axis for DXF format
        obsColumnData.push({index: i, X: inch2feet(element.x), Y: inch2feet(element.y)});
    }

    let noOfRows = obsColumnData.length;
    let totalColWidth = cellWidths.reduce((acc, width) => acc + width, 0);
    let headerText = `${label}-EDGES`;

    // Draw table components
    addTableHeader(tableStartY, cellHeight, tableStartX, totalColWidth, cellWidths, drawing, headerText);
    drawColumns(tableStartX, tableStartY, cellWidths, cellHeight, noOfRows, drawing);
    drawRows(tableStartX, tableStartY, cellHeight, totalColWidth, noOfRows, drawing);
    addColumnData(obsColumnData, cellWidths, tableStartX, tableStartY, cellHeight, drawing);

    return [totalColWidth + 12, noOfRows + 1]; // Return used space
}

// Draws the obstruction details table (dormers + obstacles)
export function ObstacleDetailTable(label, data, tableStartX, tableStartY, cellHeight, cellWidths, drawing) {

    let obsColumnData = getObsPointsData(data); // Prepare formatted data
    let totalColWidth = cellWidths.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    let noOfRows = obsColumnData.length;
    let headerText = `${label}-OBSTRUCTIONS`;

    // Draw table components
    addTableHeader(tableStartY, cellHeight, tableStartX, totalColWidth, cellWidths, drawing, headerText);
    drawColumns(tableStartX, tableStartY, cellWidths, cellHeight, noOfRows, drawing);
    drawRows(tableStartX, tableStartY, cellHeight, totalColWidth, noOfRows, drawing);
    addColumnData(obsColumnData, cellWidths, tableStartX, tableStartY, cellHeight, drawing);

    return (((data[0].length + 2 + data[2].length) * 11) + 10); // Approx space used
}

// Combines dormer and obstruction data into a single list
function getObsPointsData(data) {
    let ObsData = [];
    let sno = 0;

    ObsData.push({name: 'Obstructions', lengthRadius: 'Len/Dia', width: 'Width', X: 'X', Y: 'Y'}); // Header row

    let dormerData = data[2]; // Extract dormers
    for (let index = 0; index < dormerData.length; index++) {
        let x = (dormerData[index][0].x).toFixed(2).toString();
        let y = (dormerData[index][0].y).toFixed(2).toString();
        let dimensions = calculateLengthWidth(dormerData[index]);
        ObsData.push({
            name: `P${sno}`,
            lengthRadius: dimensions.l,
            width: dimensions.w,
            X: inch2feet(x),
            Y: inch2feet(y)
        });
        sno++;
    }

    let obstructions = data[0]; // Extract obstructions
    for (let index = 0; index < obstructions.length; index++) {
        let x = '';
        let y = '';
        if (data[1].length > 0) {
            x = (data[1][index].polygon[0].x).toFixed(2).toString();
            y = ((data[1][index].polygon[0].y)).toFixed(2).toString();
            let dimensions = calculateLengthWidth(data[1][index].polygon);
            ObsData.push({
                name: `P${sno}`,
                lengthRadius: dimensions.l,
                width: dimensions.w,
                X: inch2feet(x),
                Y: inch2feet(y)
            });
            sno++;
        }
    }
    return ObsData;
}

// Draws a summary table for total obstruction types
export function TotalObsTable(data, tableStartX, tableStartY, cellHeight, cellWidths, drawing) {
    let obsColumnData = [];

    let totalObs = data.length;
    let totalRecObs = data.filter(item => item.type === 'rectangle').length;
    let totalCirObs = data.filter(item => item.type === 'circle').length;

    // Header + data rows
    obsColumnData.push({name: 'NAME', count: 'COUNT'});
    obsColumnData.push({name: 'Rectangle Obs', count: totalRecObs.toString()});
    obsColumnData.push({name: 'Circle Obs', count: totalCirObs.toString()});
    obsColumnData.push({name: 'Total Obstruction', count: totalObs.toString()});

    let totalColWidth = cellWidths.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    let noOfRows = 4;
    let headerText = 'OBSTRUCTIONS-DETAILS';

    // Draw table
    addTableHeader(tableStartY, cellHeight, tableStartX, totalColWidth, cellWidths, drawing, headerText);
    drawColumns(tableStartX, tableStartY, cellWidths, cellHeight, noOfRows, drawing);
    drawRows(tableStartX, tableStartY, cellHeight, totalColWidth, noOfRows, drawing);
    addColumnData(obsColumnData, cellWidths, tableStartX, tableStartY, cellHeight, drawing);
}

// Draws the areas table with coverage breakdown
export function AreasTable(data, tableStartX, tableStartY, cellHeight, cellWidths, drawing) {
    let obsColumnData = [];

    // Header + area data rows
    obsColumnData.push({area: 'AREA', units: 'UNITS(sqft)', percent: 'PERCENTAGE(%)'});
    obsColumnData.push({area: 'Total', units: data.total.toString(), percent: '100'});
    obsColumnData.push({area: 'Available', units: data.available.toString(), percent: (data.available / data.total * 100).toFixed(2).toString()});
    obsColumnData.push({area: 'Covered', units: data.covered.toFixed(2).toString(), percent: (data.covered / data.total * 100).toFixed(2).toString()});
    obsColumnData.push({area: 'Remaining', units: data.remaining.toFixed(2).toString(), percent: (data.remaining / data.total * 100).toFixed(2).toString()});

    let totalColWidth = cellWidths.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    let noOfRows = 5;
    let headerText = 'AREA-DETAILS';

    // Draw table
    addTableHeader(tableStartY, cellHeight, tableStartX, totalColWidth, cellWidths, drawing, headerText);
    drawColumns(tableStartX, tableStartY, cellWidths, cellHeight, noOfRows, drawing);
    drawRows(tableStartX, tableStartY, cellHeight, totalColWidth, noOfRows, drawing);
    addColumnData(obsColumnData, cellWidths, tableStartX, tableStartY, cellHeight, drawing);

    return noOfRows + 1;
}

// Draws table header row with label centered above the table
function addTableHeader(tableStartY, cellHeight, tableStartX, totalColWidth, cellWidth, drawing, headerText) {
    const headingRowY = tableStartY + cellHeight;
    let mid = point3d(tableStartX + totalColWidth / 2, headingRowY - cellHeight / 2, 0.6);

    // Draw top line and label
    drawing.addLine(point3d(tableStartX, headingRowY, 0), point3d(tableStartX + cellWidth.reduce((acc, width) => acc + width, 0), headingRowY, 0));
    drawing.addText(mid, 6, headerText, {
        colorNumber: 141,
        horizontalAlignment: TextHorizontalAlignment.Center,
        verticalAlignment: TextVerticalAlignment.Middle,
        secondAlignmentPoint: mid
    });
}

// Draws vertical lines between columns
function drawColumns(tableStartX, tableStartY, cellWidth, cellHeight, noOfRows, drawing) {
    for (let i = 0; i <= 5; i++) {
        const x = tableStartX + cellWidth.slice(0, i).reduce((acc, width) => acc + width, 0);
        if (i === 5 || i === 0) {
            drawing.addLine(point3d(x, tableStartY + 11, 0), point3d(x, tableStartY - (noOfRows * cellHeight), 0));
        } else {
            drawing.addLine(point3d(x, tableStartY, 0), point3d(x, tableStartY - (noOfRows * cellHeight), 0));
        }
    }
}

// Draws horizontal lines between rows
function drawRows(tableStartX, tableStartY, cellHeight, cellWidth, noOfRows, drawing) {
    for (let i = 0; i <= noOfRows; i++) {
        const y = tableStartY - i * cellHeight;
        drawing.addLine(point3d(tableStartX, y, 0), point3d(tableStartX + cellWidth, y, 0));
    }
}

// Fills table cells with text content
function addColumnData(data, cellWidths, tableStartX, tableStartY, cellHeight, drawing) {
    data.forEach((dataObj, index) => {
        const keyCellCenterX = cellWidths.map((width, i) => tableStartX + cellWidths.slice(0, i).reduce((acc, w) => acc + w, 0) + width / 2);
        const cellCenterY = tableStartY - index * cellHeight - cellHeight / 2;
        Object.entries(dataObj).forEach(([key, value], i) => {
            const mid = point3d(keyCellCenterX[i], cellCenterY, 0.6);
            drawing.addText(mid, 4.5, value, {
                colorNumber: i === 0 ? 141 : 50,
                horizontalAlignment: TextHorizontalAlignment.Center,
                verticalAlignment: TextVerticalAlignment.Middle,
                secondAlignmentPoint: mid,
            });
        });

    });
}
